/**
 * 🚫 GLOBAL RULE:
 * NO HARDCODED LOCATIONS.
 * Location must ALWAYS come from user input.
 * Violations will break spatial integrity.
 */

/**
 * Wingman ModeController
 * Orchestrates spatial runtime modes with lifecycle hooks.
 * Only one mode may be active at a time.
 */

import { AudioEngine } from './AudioEngine';
import { MusicEngine } from './MusicEngine';
import { contextEngine } from '../context/contextEngine';

// --- Stability Guard ---
let switchTimestamps = [];
const MAX_SWITCHES = 3;
const WINDOW_MS = 10000;
const COOLDOWN_MS = 5000;
let cooldownActive = false;

function checkStabilityGuard() {
    const now = Date.now();
    switchTimestamps = switchTimestamps.filter(t => now - t < WINDOW_MS);
    
    if (cooldownActive) {
        console.warn("[ModeController] Cooldown active — switch blocked");
        return false;
    }
    
    if (switchTimestamps.length >= MAX_SWITCHES) {
        console.warn("[ModeController] Rate limit hit (3/10s) — activating 5s cooldown");
        cooldownActive = true;
        setTimeout(() => {
            cooldownActive = false;
            console.info("[ModeController] Cooldown expired — switches allowed");
        }, COOLDOWN_MS);
        return false;
    }
    
    switchTimestamps.push(now);
    return true;
}

// --- Booth Mode ---
const BoothMode = {
    name: "BOOTH",
    start() {
        console.info("[Mode:BOOTH] Starting — kiosk demo display");
        if (window.environmentController) {
            window.environmentController.setEnvironment("booth");
        }
        if (window.contextState) {
            window.contextState.setPanelMode("booth");
        }
    },
    stop() {
        console.info("[Mode:BOOTH] Stopping");
        if (window.contextState) {
            window.contextState.setPanelMode("none");
        }
    }
};

// --- Lobby Mode (DYNAMIC) ---
const LobbyMode = {
    name: "LOBBY",
    _intervalId: null,
    _sequence: [
        { env: "penthouse", coords: [0, 20], label: "Global HQ" },
        // Sequences should be populated dynamically from user data in production
    ],
    _currentIndex: 0,

    start() {
        console.info("[Mode:LOBBY] Starting — auto-rotate lobby display");
        this._currentIndex = 0;
        this._runStep();
        this._intervalId = setInterval(() => this._runStep(), 15000);
    },

    _runStep() {
        const step = this._sequence[this._currentIndex % this._sequence.length];
        console.info(`[Mode:LOBBY] → ${step.label} (${step.env})`);
        
        if (window.environmentController) {
            window.environmentController.setEnvironment(step.env);
        }
        if (window.WingmanSpatialControl && window.WingmanSpatialControl.syncSpatialState) {
            window.WingmanSpatialControl.syncSpatialState({
                center: { lon: step.coords[0], lat: step.coords[1] },
                markers: []
            });
        }
        this._currentIndex++;
    },

    stop() {
        console.info("[Mode:LOBBY] Stopping — clearing interval");
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        this._currentIndex = 0;
    }
};

// --- Driver Mode ---
const DriverMode = {
    name: "DRIVER",
    _watchId: null,

    start() {
        console.info("[Mode:DRIVER] Starting — GPS HUD active");
        
        // Disable luxury backdrop for map visibility
        if (window.environmentController) {
            window.environmentController.setEnvironment("original");
        }
        if (window.contextState) {
            window.contextState.setPanelMode("map");
            window.contextState.setData({ 
                ...window.contextState.getData?.() || {},
                mapMode: true 
            });
        }
        
        // 🚀 ATOM S3: Start GPS tracking via Unified Authority
        contextEngine.startTracking();
    },

    stop() {
        console.info("[Mode:DRIVER] Stopping — clearing GPS watch");
        contextEngine.stopTracking();
    }
};

// --- Default Mode (passive) ---
const DefaultMode = {
    name: "DEFAULT",

    start() {
        console.info("[Mode:DEFAULT] Restoring luxury experience");
        if (window.environmentController) {
            window.environmentController.resetToDefault();
        }
        if (window.contextState) {
            window.contextState.setPanelMode("none");
        }
    },
    stop() {
        // Nothing to clean up in default mode
    }
};

// --- Tour Mode (Interactive Holographic Onboarding — WINGMAN-ONBOARDING-001) ---
// Note: This visual control tour strictly has ZERO spatial or environment side effects.
// Advancing, returning, skipping, finishing, or restarting preserves camera, markers, environment and radio playback.
const TourMode = {
    name: "TOUR",
    _intervalId: null,
    _currentIndex: 0,
    _sequence: [
        {
            current: 1,
            total: 5,
            selector: '[data-testid="voice-trigger"]',
            arrowDirection: 'down',
            label: "Phase 1 / 5 — Voice Copilot",
            title: "Microphone Activation & Voice Control",
            caption: "Tap the 'Talk to Wingman' button below to activate your microphone. Once active, speak naturally: say 'Fly to Tokyo', 'Play jazz', or 'Open showroom'. Every feature also offers complete manual control."
        },
        {
            current: 2,
            total: 5,
            selector: '[data-testid="nav-cockpit-btn"]',
            arrowDirection: 'down',
            label: "Phase 2 / 5 — 3D Spatial Cockpit",
            title: "3D Earth Navigation & Globe Controls",
            caption: "Your full-screen cockpit provides interactive 3D Earth mapping. Pan, tilt, and zoom manually with your mouse or touch, or speak 'Fly to Paris' or 'Fly to Miami' for automated spatial navigation."
        },
        {
            current: 3,
            total: 5,
            selector: '[data-testid="nav-travel-btn"]',
            arrowDirection: 'down',
            label: "Phase 3 / 5 — Workspace Authority",
            title: "Travel & Real Estate Intelligence",
            caption: "Switch between curated worldwide flight deals and high-end property command centers. Say 'Open showroom' or 'Pull up real estate', or manually select workspaces from the top navigation bar."
        },
        {
            current: 4,
            total: 5,
            selector: '[data-testid="nav-radio-btn"]',
            arrowDirection: 'down',
            label: "Phase 4 / 5 — Wingman Radio",
            title: "Live Global Internet Radio",
            caption: "Stream live worldwide radio stations. Free manual listening; station availability varies. Manual playback, station switching, and volume controls are free. AI voice commands ('Play house', 'Pause', 'Resume', 'Change station') consume your voice quota or active plan allowance."
        },
        {
            current: 5,
            total: 5,
            selector: '[data-testid="nav-help-header-btn"], [data-testid="mobile-help-btn"], [data-testid="nav-help-btn"]',
            arrowDirection: 'down',
            label: "Phase 5 / 5 — Operational Intel",
            title: "Command Cheatsheet & Help Center",
            caption: "Tap Help above or say 'Open cheatsheet' anytime for a full reference of voice commands, gestures, and system status. Press ESC to return to your cockpit anytime."
        }
    ],

    start(startIndex = 0) {
        console.info("[Mode:TOUR] Starting — Guided Pilot Tour Active");
        this._currentIndex = typeof startIndex === 'number' ? startIndex : 0;
        this._runStep();
    },

    setStep(index) {
        if (index < 0) index = 0;
        if (index >= this._sequence.length) index = this._sequence.length - 1;
        this._currentIndex = index;
        this._runStep();
    },

    nextStep() {
        if (this._currentIndex < this._sequence.length - 1) {
            this._currentIndex++;
            this._runStep();
        } else {
            this.stop();
            if (typeof ModeController !== 'undefined') {
                ModeController.setMode('DEFAULT');
            }
        }
    },

    prevStep() {
        if (this._currentIndex > 0) {
            this._currentIndex--;
            this._runStep();
        }
    },

    _runStep() {
        const step = this._sequence[this._currentIndex];
        console.info(`[Mode:TOUR] → Phase ${step.current}: ${step.label}`);
        // Zero spatial, camera, marker, or environment side effects.
        // Preserves active navigation, radio playback, and user state entirely.
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('wingman:tour:step', { detail: step }));
        }
    },

    stop() {
        console.info("[Mode:TOUR] Stopping — cleaning tour");
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('wingman:tour:end'));
        }
    }
};

// --- Destination Tour Mode (Separate spatial destination showcase) ---
const DestinationTourMode = {
    name: "DESTINATION_TOUR",
    _intervalId: null,
    _currentIndex: 0,
    _sequence: [
        { label: "Ocean Drive", coords: [-80.13, 25.78], env: "sunset" },
        { label: "Wynwood Arts", coords: [-80.19, 25.80], env: "night" },
        { label: "Brickell Hub", coords: [-80.19, 25.76], env: "morning" }
    ],

    start() {
        console.info("[Mode:DESTINATION_TOUR] Starting — Destination Tour Active");
        this._currentIndex = 0;
        this._runStep();
        this._intervalId = setInterval(() => this._runStep(), 12000);
    },

    _runStep() {
        const step = this._sequence[this._currentIndex % this._sequence.length];
        console.info(`[Mode:DESTINATION_TOUR] → Visiting ${step.label}`);
        if (window.environmentController && step.env) {
            window.environmentController.setEnvironment(step.env);
        }
        if (window.WingmanSpatialControl && window.WingmanSpatialControl.syncSpatialState && step.coords) {
            window.WingmanSpatialControl.syncSpatialState({
                center: { lon: step.coords[0], lat: step.coords[1] },
                markers: [{ name: step.label, rating: 5.0, address: "Elite Destination" }]
            });
        }
        this._currentIndex++;
    },

    stop() {
        console.info("[Mode:DESTINATION_TOUR] Stopping — cleaning interval");
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }
};

// --- Mode Registry ---
const modes = {
    DEFAULT: DefaultMode,
    BOOTH: BoothMode,
    LOBBY: LobbyMode,
    DRIVER: DriverMode,
    TOUR: TourMode,
    DESTINATION_TOUR: DestinationTourMode,
};


// --- Mode Controller ---
let currentMode = DefaultMode;
let currentModeName = "DEFAULT";

export const ModeController = {
    modes,
    TourMode,
    get modes() { return modes; },
    getActiveMode() {
        return currentModeName;
    },

    setMode(modeName) {
        const upperName = modeName.toUpperCase();
        
        if (upperName === currentModeName) {
            console.info(`[ModeController] Already in ${upperName} mode`);
            return true;
        }

        // Stability Guard check
        if (!checkStabilityGuard()) {
            return false;
        }

        const nextMode = modes[upperName];
        if (!nextMode) {
            console.error(`[ModeController] Unknown mode: ${upperName}`);
            return false;
        }

        // Mode Conflict Guard: stop current, then start next
        console.info(`[ModeController] ${currentModeName} → ${upperName}`);
        
        // 1. Stop current mode (teardown)
        currentMode.stop();

        // 2. Reset spatial state machine to IDLE
        if (window.WingmanStateMachine) {
            window.WingmanStateMachine.transition("IDLE", "SYNC");
        }

        // 2.5 Play Cinematic Feedback & Sync Vibe
        AudioEngine.playSwitch();
        MusicEngine.setVibeFromMode(upperName);

        // 3. Start next mode

        nextMode.start();
        currentMode = nextMode;
        currentModeName = upperName;

        // 4. Update Context State
        if (window.contextState) {
            window.contextState.setActiveMode(upperName);
            window.contextState.setStayInRegion(false); // 🛡️ Mode switch clears region lock
        }

        // 5. Log to trace system
        if (window.WingmanTraceRecorder) {
            window.WingmanTraceRecorder.recordModeSwitch(currentModeName, upperName);
        }

        return true;
    },

    reset() {
        switchTimestamps = [];
        cooldownActive = false;
        this.setMode("DEFAULT");
    },


    isCooldownActive() {
        return cooldownActive;
    },

    getSwitchCount() {
        const now = Date.now();
        return switchTimestamps.filter(t => now - t < WINDOW_MS).length;
    }
};

// Expose globally
if (typeof window !== 'undefined') {
    window.ModeController = ModeController;
}

export { TourMode, modes };
export default ModeController;
